import { detectStalls, checkDeloadNeeded, fatigueRatio, plateauPredictor, filterWarmupSets, calcAvg1RM, calcVolume } from './science';

export const COACH_SYSTEM_PROMPT = `Sei un coach di bodybuilding science-based che scrive analisi per atleti amatori seri.

Struttura OBBLIGATORIA per ogni risposta:

**[Verdetto]**
1-2 frasi dirette su cosa sta succedendo. Prendi posizione netta, non essere neutro.

**[Esercizi]**
Per ogni esercizio usa ESATTAMENTE questo formato (una riga per campo, senza asterischi, senza bullet):

NOME ESERCIZIO
Stato: Progressione | Stallo | Regressione | PR
Analisi: una frase sull'andamento basata sui numeri
Causa: (solo per Stallo/Regressione) causa probabile in una frase
Obiettivo: SEMPRE inizia con un verbo d'azione: "Aumenta a Xkg, obiettivo Y reps per S1." | "Riduci a Xkg, obiettivo Y reps per S1." | "Mantieni Xkg, porta le reps a Y per S1." | "Scala a Xkg, obiettivo Y reps per S1." — NON scrivere solo il numero, il verbo è obbligatorio. NON ripetere l'ultimo valore, indica sempre il prossimo step concreto diverso.

Analizza TUTTI gli esercizi nei dati, anche quelli in progressione.

**[Piano prossima sessione]**
2-4 azioni specifiche con numeri esatti. Es: "Hack Squat: 62.5kg, obiettivo 8 reps."

**[Alert]**
Solo se c'è un pattern trasversale preoccupante (volume in calo generale, stalli multipli, progressione insostenibile). Ometti altrimenti.

REGOLE LOGICHE TASSATIVE (ANTI-ALLUCINAZIONE):
1. DIVIETO DI AGGREGAZIONE ERRATA: Non sommare MAI le ripetizioni totali se i set hanno carichi diversi. Se S1:20kg×7, S2:15kg×10, S3:15kg×9 allora il totale a 20kg è solo 7 reps, non 26.
2. CONFRONTO A PARITÀ DI CARICO: Puoi affermare che c'è stato un calo o un aumento "a parità di carico" SOLO confrontando le ripetizioni eseguite con l'esatto stesso peso in entrambe le sessioni.
3. ANALISI DEL TOP SET: Dai sempre priorità al set 1 (S1). Se il carico massimo è uguale ma le reps nel S1 scendono, segnala un calo di performance sul Top Set.
4. DROP SET E SCALING: Se l'utente abbassa il carico nei set successivi, riconosci che ha scalato il peso per mantenere il volume o per cedimento. Non considerarlo un errore, ma analizzane l'impatto sul volume totale (tonnellaggio).
5. PRESCRIZIONE CON CARICO RIDOTTO: Se S1 weight nell'ultima sessione è INFERIORE alla sessione precedente, NON prescrivere mai un carico superiore a quello della sessione precedente. Prima consolida il carico attuale aumentando le reps, poi proponi di risalire al carico precedente. Esempio: se era 20kg e ora è 17kg, obiettivo è "17kg, porta S1 a 12 reps" — NON "aumenta a 20kg".

Regole di stile:
- Italiano diretto, come un coach che parla all'atleta faccia a faccia
- SOLO i dati numerici forniti nel contesto, niente consigli generici
- Niente frasi consolatorie o motivazionali
- Non menzionare RPE, i dati non la includono
- Citazioni scientifiche solo se cambiano concretamente la prescrizione
- Massimo 600 parole`;

function fmt(n, dec = 1) {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toFixed(dec);
}

function lastNSessions(sessions, n = 3, filterName = null) {
  let sorted = [...sessions].sort((a, b) => b.date.localeCompare(a.date));
  if (filterName) {
    const name = filterName.toLowerCase().trim();
    sorted = sorted.filter(s => s.exercises.some(e => e.name.toLowerCase().trim() === name));
  }
  return sorted.slice(0, n);
}

function sessionSummaryCompact(sess) {
  const exList = sess.exercises.map(ex => {
    const working = filterWarmupSets(ex.sets || []);
    const s1 = working.find(s => parseFloat(s.weight) > 0);
    const vol = Math.round(calcVolume(ex.sets || []));
    const rm1 = fmt(calcAvg1RM(ex.sets || []));
    return `  - ${ex.name}: S1=${s1 ? `${parseFloat(s1.weight)}kg×${s1.reps}` : '—'} vol=${vol} 1RM≈${rm1}`;
  }).join('\n');
  return `${sess.workoutName || ''}(${sess.date})\n${exList}`;
}

function sessionSummary(sess) {
  const date = sess.date;
  const name = sess.workoutName || 'Allenamento';
  const totalVol = Math.round(sess.exercises.reduce((s, e) => s + calcVolume(e.sets || []), 0));
  const exList = sess.exercises.map(ex => {
    const working = filterWarmupSets(ex.sets || []);
    const rm1 = fmt(calcAvg1RM(ex.sets || []));
    const setDetail = working
      .filter(s => parseInt(s.reps) > 0)
      .map((s, i) => `S${i + 1}:${parseFloat(s.weight) || 0}kg×${s.reps}`)
      .join(' ');
    const totalReps = working.reduce((s, x) => s + (parseInt(x.reps) || 0), 0);
    const vol = Math.round(calcVolume(ex.sets || []));
    return `  - ${ex.name}: [${setDetail}] tot ${totalReps} reps, vol ${vol}kg, 1RM ~${rm1}kg`;
  }).join('\n');
  return `${name} (${date}) | Volume: ${totalVol}\n${exList}`;
}

export function buildGeminiContext(sessions, type, options = {}) {
  const lines = [];

  if (type === 'session') {
    const sorted = [...sessions].sort((a, b) => b.date.localeCompare(a.date));
    if (!sorted.length) return { prompt: 'Nessuna sessione disponibile.', system: COACH_SYSTEM_PROMPT };

    // options.schedaName forza l'analisi su una scheda specifica (es. da Dashboard)
    const schedaName = options.schedaName || sorted[0].workoutName || null;

    // Tutte le sessioni della STESSA scheda, max 12 — sessioni 0-1 complete, 2-11 formato compatto
    const sameName = schedaName
      ? sorted.filter(s => s.workoutName === schedaName)
      : sorted;
    const last = sameName.slice(0, 12);

    const deload = checkDeloadNeeded(sessions);

    lines.push(`=== STORICO: ${schedaName || 'Allenamento'} (${last.length} sessioni) ===`);
    last.forEach((s, idx) => {
      lines.push(idx === 0 ? `\n[SESSIONE PIU RECENTE - ${s.date}]` : `\n[${idx} sessioni fa - ${s.date}]`);
      lines.push(idx <= 1 ? sessionSummary(s) : sessionSummaryCompact(s));
    });

    // Delta esercizi tra ultima e precedente
    if (last.length >= 2) {
      const curr = last[0];
      const prev = last[1];
      lines.push('\n=== DELTA RISPETTO ALLA SESSIONE PRECEDENTE ===');
      curr.exercises.forEach(ex => {
        const prevEx = prev.exercises.find(e => e.name === ex.name);
        if (!prevEx) return;
        const currWorking = filterWarmupSets(ex.sets || []);
        const prevWorking = filterWarmupSets(prevEx.sets || []);
        const currReps = currWorking.reduce((s, x) => s + (parseInt(x.reps) || 0), 0);
        const prevReps = prevWorking.reduce((s, x) => s + (parseInt(x.reps) || 0), 0);
        const currVol = Math.round(calcVolume(ex.sets || []));
        const prevVol = Math.round(calcVolume(prevEx.sets || []));
        const currDetail = currWorking.filter(x => parseInt(x.reps) > 0).map((x, i) => `S${i+1}:${parseFloat(x.weight)||0}kg×${x.reps}`).join(' ');
        const prevDetail = prevWorking.filter(x => parseInt(x.reps) > 0).map((x, i) => `S${i+1}:${parseFloat(x.weight)||0}kg×${x.reps}`).join(' ');
        const rDiff = currReps - prevReps;
        const vDiff = currVol - prevVol;
        lines.push(`  ${ex.name}: [${prevDetail}]→[${currDetail}] rep ${prevReps}→${currReps}(${rDiff>=0?'+':''}${rDiff}) vol ${prevVol}→${currVol}kg(${vDiff>=0?'+':''}${vDiff}kg)`);
      });
    }

    // Classificazione pre-calcolata — moving average su 4 sessioni (2 curr vs 2 prev)
    if (last.length >= 2) {
      const getExMetrics = (sess, exName) => {
        const ex = sess.exercises.find(e => e.name === exName);
        if (!ex) return null;
        const working = filterWarmupSets(ex.sets || []);
        const s1 = working.find(s => parseFloat(s.weight) > 0);
        return {
          s1w: parseFloat(s1?.weight || 0),
          s1r: parseInt(s1?.reps || 0),
          vol: calcVolume(ex.sets || []),
          reps: working.reduce((s, x) => s + (parseInt(x.reps) || 0), 0),
        };
      };
      const avgMetrics = (sessions4, exName) => {
        const metrics = sessions4.map(s => getExMetrics(s, exName)).filter(Boolean);
        if (!metrics.length) return null;
        return {
          s1w: metrics.reduce((s, m) => s + m.s1w, 0) / metrics.length,
          s1r: metrics.reduce((s, m) => s + m.s1r, 0) / metrics.length,
          vol: metrics.reduce((s, m) => s + m.vol, 0) / metrics.length,
          reps: metrics.reduce((s, m) => s + m.reps, 0) / metrics.length,
        };
      };

      const use4 = last.length >= 4;
      const currSessions = use4 ? [last[0], last[1]] : [last[0]];
      const prevSessions = use4 ? [last[2], last[3]] : [last[1]];

      lines.push('\n=== CLASSIFICAZIONE PRE-CALCOLATA (usa ESATTAMENTE questi stati) ===');
      last[0].exercises.forEach(ex => {
        const curr = avgMetrics(currSessions, ex.name);
        const prev = avgMetrics(prevSessions, ex.name);
        if (!curr || !prev) return;
        const volPct = prev.vol > 0 ? Math.round((curr.vol - prev.vol) / prev.vol * 100) : 0;
        const s1WeightUp = curr.s1w > prev.s1w;
        const s1WeightDown = curr.s1w < prev.s1w;
        const s1RepsUp = curr.s1r > prev.s1r;
        const volUp = volPct >= 5;
        const volDown = volPct <= -5;
        let stato;
        if (s1WeightUp) stato = 'PR';
        else if (volUp || (s1RepsUp && !s1WeightDown)) stato = 'Progressione';
        else if (s1WeightDown && volDown) stato = 'Regressione';
        else stato = 'Stallo';
        const window = use4 ? '(media 2+2 sessioni)' : '(2 sessioni)';
        lines.push(`  ${ex.name}: Stato=${stato} ${window} | S1 ${fmt(prev.s1w,1)}kg×${fmt(prev.s1r,0)}→${fmt(curr.s1w,1)}kg×${fmt(curr.s1r,0)} | Vol ${fmt(prev.vol,0)}→${fmt(curr.vol,0)}kg(${volPct>=0?'+':''}${volPct}%)`);
      });
    }

    // Segnali longitudinali (fatica + plateau trend)
    if (last.length >= 2) {
      const longitudinal = [];
      last[0].exercises.forEach(ex => {
        const fr = fatigueRatio(ex.name, sessions);
        const pp = plateauPredictor(ex.name, sessions);
        if (!fr && !pp) return;
        const parts = [];
        if (fr) parts.push(`fatica: -${fmt(fr.drop, 0)}% vol vs media (${fr.status})`);
        if (pp) parts.push(`tendenza 1RM: ${fmt(pp.avg, 2)}%/ses su ${pp.n} sessioni (${pp.status})`);
        longitudinal.push(`  ${ex.name}: ${parts.join(' | ')}`);
      });
      if (longitudinal.length) {
        lines.push('\n=== SEGNALI LONGITUDINALI ===');
        longitudinal.forEach(l => lines.push(l));
      }
    }

    if (deload.needed) lines.push(`\nDeload: ${deload.weeksSinceDeload} settimane consecutive senza scarico.`);

    lines.push(`\nUSA OBBLIGATORIAMENTE gli stati della sezione CLASSIFICAZIONE PRE-CALCOLATA — non ricalcolare lo stato da zero. Analizza causa e prescrizione per ogni esercizio della sessione ${schedaName || ''} del ${last[0]?.date || ''}.`);
  }

  else if (type === 'exercise') {
    const exName = options.exerciseName;
    if (!exName) return { prompt: 'Nessun esercizio selezionato.', system: COACH_SYSTEM_PROMPT };

    const last = lastNSessions(sessions, 6, exName);
    if (!last.length) return { prompt: `Nessuna sessione trovata per "${exName}".`, system: COACH_SYSTEM_PROMPT };

    const fr = fatigueRatio(exName, sessions);
    const pp = plateauPredictor(exName, sessions);

    lines.push(`=== STORICO: ${exName} ===`);
    last.forEach(s => {
      const ex = s.exercises.find(e => e.name.toLowerCase().trim() === exName.toLowerCase().trim());
      if (!ex) return;
      const working = filterWarmupSets(ex.sets || []);
      const rm1 = fmt(calcAvg1RM(ex.sets || []));
      const vol = Math.round(calcVolume(ex.sets || []));
      const setDetail = working
        .filter(x => parseInt(x.reps) > 0)
        .map((x, i) => `S${i + 1}:${parseFloat(x.weight) || 0}kg×${x.reps}`)
        .join(' ');
      lines.push(`${s.date}: [${setDetail}] vol ${vol}kg, 1RM ~${rm1}kg`);
    });

    if (fr) lines.push(`\nFatica: volume attuale ${fr.curr} = ${fmt(100 - fr.drop, 0)}% della media (${fr.avg})`);
    if (pp) lines.push(`Plateau predictor: crescita media ${fmt(pp.avg, 2)}%/sessione su ${pp.n} occorrenze (stato: ${pp.status})`);

    lines.push(`\nBASANDOTI ESCLUSIVAMENTE sui numeri sopra: analizza la progressione di ${exName}. Stallo, regressione o crescita? Prescrivi peso esatto e rep range per le prossime 2 sessioni.`);
  }

  else if (type === 'deload') {
    const deload = checkDeloadNeeded(sessions);
    const stalls = detectStalls(sessions);
    const recent = lastNSessions(sessions, 4);

    lines.push('=== ANALISI DELOAD ===');
    lines.push(`Settimane consecutive senza scarico: ${deload.weeksSinceDeload}`);
    lines.push(`Deload necessario secondo soglia (5+ settimane): ${deload.needed ? 'SI' : 'NO'}`);
    if (stalls.length) lines.push(`Esercizi in stallo: ${stalls.join(', ')}`);

    lines.push('\nUltime sessioni (volume):');
    recent.forEach(s => {
      const vol = Math.round(s.exercises.reduce((acc, e) => acc + calcVolume(e.sets || []), 0));
      lines.push(`  ${s.date} ${s.workoutName || ''}: volume ${vol}`);
    });

    lines.push('\nValuta se serve un deload e, in caso, prescivi una settimana di scarico specifica (volume %, esercizi da ridurre).');
  }

  return { prompt: lines.join('\n'), system: COACH_SYSTEM_PROMPT };
}

export function buildFreeTextContext(sessions, userMessage) {
  if (!sessions.length) return { prompt: userMessage, system: COACH_SYSTEM_PROMPT };

  const sorted = [...sessions].sort((a, b) => b.date.localeCompare(a.date));
  const lines = [];

  // Trova la scheda più rilevante rispetto al messaggio utente
  const allNames = [...new Set(sessions.map(s => s.workoutName).filter(Boolean))];
  const msgLower = userMessage.toLowerCase();
  const matchedScheda = allNames.find(name =>
    name.toLowerCase().split(/\s+|[^a-z]/i).some(word => word.length > 3 && msgLower.includes(word))
  );

  if (matchedScheda) {
    // Tutte le sessioni della scheda, max 8
    const sameName = sorted.filter(s => s.workoutName === matchedScheda);
    const last = sameName.slice(0, 8);

    lines.push(`=== STORICO: ${matchedScheda} (${last.length} sessioni) ===`);
    last.forEach((s, idx) => {
      lines.push(idx === 0 ? `\n[SESSIONE PIU RECENTE - ${s.date}]` : `\n[${idx} sessioni fa - ${s.date}]`);
      lines.push(sessionSummary(s));
    });

    if (last.length >= 2) {
      const curr = last[0];
      const prev = last[1];
      lines.push('\n=== DELTA RISPETTO ALLA SESSIONE PRECEDENTE ===');
      curr.exercises.forEach(ex => {
        const prevEx = prev.exercises.find(e => e.name === ex.name);
        if (!prevEx) return;
        const currW = Math.max(0, ...filterWarmupSets(ex.sets || []).map(s => parseFloat(s.weight) || 0));
        const prevW = Math.max(0, ...filterWarmupSets(prevEx.sets || []).map(s => parseFloat(s.weight) || 0));
        const currReps = filterWarmupSets(ex.sets || []).reduce((acc, x) => acc + (parseInt(x.reps) || 0), 0);
        const prevReps = filterWarmupSets(prevEx.sets || []).reduce((acc, x) => acc + (parseInt(x.reps) || 0), 0);
        const wDiff = currW - prevW;
        const rDiff = currReps - prevReps;
        const tag = wDiff > 0 ? `+${wDiff}kg` : wDiff < 0 ? `${wDiff}kg` : rDiff > 0 ? `+${rDiff} reps` : rDiff < 0 ? `${rDiff} reps` : 'invariato';
        lines.push(`  ${ex.name}: ${tag} (${prevW}kg/${prevReps}reps → ${currW}kg/${currReps}reps)`);
      });
    }

    if (last.length >= 2) {
      const curr = last[0];
      const prev = last[1];
      lines.push('\n=== CLASSIFICAZIONE PRE-CALCOLATA (usa ESATTAMENTE questi stati) ===');
      curr.exercises.forEach(ex => {
        const prevEx = prev.exercises.find(e => e.name === ex.name);
        if (!prevEx) return;
        const currWorking = filterWarmupSets(ex.sets || []);
        const prevWorking = filterWarmupSets(prevEx.sets || []);
        const currS1W = parseFloat(currWorking.find(s => parseFloat(s.weight) > 0)?.weight || 0);
        const prevS1W = parseFloat(prevWorking.find(s => parseFloat(s.weight) > 0)?.weight || 0);
        const currS1R = parseInt(currWorking.find(s => parseFloat(s.weight) > 0)?.reps || 0);
        const prevS1R = parseInt(prevWorking.find(s => parseFloat(s.weight) > 0)?.reps || 0);
        const currVol = Math.round(calcVolume(ex.sets || []));
        const prevVol = Math.round(calcVolume(prevEx.sets || []));
        const currReps = currWorking.reduce((s, x) => s + (parseInt(x.reps) || 0), 0);
        const prevReps = prevWorking.reduce((s, x) => s + (parseInt(x.reps) || 0), 0);
        const volPct = prevVol > 0 ? Math.round((currVol - prevVol) / prevVol * 100) : 0;
        const s1WeightUp = currS1W > prevS1W;
        const s1WeightDown = currS1W < prevS1W;
        const s1RepsUp = currS1R > prevS1R;
        const volUp = volPct >= 5;
        const volDown = volPct <= -5;
        let stato;
        if (s1WeightUp) stato = 'PR';
        else if (volUp || (s1RepsUp && !s1WeightDown)) stato = 'Progressione';
        else if (s1WeightDown && volDown) stato = 'Regressione';
        else stato = 'Stallo';
        lines.push(`  ${ex.name}: Stato=${stato} | S1 ${prevS1W}kg×${prevS1R}→${currS1W}kg×${currS1R} | Vol ${prevVol}→${currVol}kg(${volPct>=0?'+':''}${volPct}%) | Reps ${prevReps}→${currReps}`);
      });
    }

    const stalls = detectStalls(sessions);
    if (stalls.length) lines.push(`\nStalli rilevati: ${stalls.join(', ')}`);

    lines.push(`\nDomanda: ${userMessage}`);
    lines.push(`\nPer ogni esercizio in stallo o regressione: indica causa probabile e prescrizione per la prossima sessione (peso esatto + reps target). Per chi progredisce: conferma e indica il prossimo step concreto.`);
  } else {
    // Nessuna scheda identificata — manda le ultime 4 sessioni come contesto generico
    const recent = sorted.slice(0, 4);
    lines.push('=== ULTIME SESSIONI ===');
    recent.forEach(s => lines.push(sessionSummary(s)));
    const stalls = detectStalls(sessions);
    if (stalls.length) lines.push(`\nStalli rilevati: ${stalls.join(', ')}`);
    lines.push(`\nDomanda: ${userMessage}`);
    lines.push('\nRispondi basandoti esclusivamente sui dati sopra. Per ogni punto: prescrizione concreta con numeri precisi.');
  }

  return { prompt: lines.join('\n'), system: COACH_SYSTEM_PROMPT };
}
